import React, { useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { Dialog, ElevatedButton, TextButton, TextInput } from '@producer/ui-kit';

import { SingleSelectDropdown } from '../../../components/select-dropdown';
import { RoleSelect } from '../../teams/components/role-select';
import { Space } from '../../../app/entities/space';
import { getCrewContactOptions } from '../lib/crew-contact-options';
import {
  createCrewMember,
  persistNewCrewPickListValues,
  updateCrewMember,
} from '../stores/crew.slice';
import { catchError } from '../../../core/catch-error';

import type { CrewMember } from '../../../app/entities/crew-member';
import type { Project } from '../../../app/entities/project';
import type { CrewMemberInput } from '../stores/crew.slice';

import styles from './crew-member.modal.module.scss';

type FormValues = {
  name?: string;
  role?: string;
  department?: string;
  team?: string;
  email?: string;
  phone?: string;
  contactId?: string;
};

type CrewMemberModalProps = {
  project: Project;
  /** Present puts the modal in edit mode; absent creates a new position. */
  member?: CrewMember;
  /**
   * Pre-filled values for create mode — e.g. `{ department: 'Camera' }` when adding straight
   * from a group header. Ignored in edit mode, which always seeds the form from `member`.
   */
  defaults?: Partial<FormValues>;
  onCancel: () => void;
  onClose: () => void;
};

/**
 * One form for both creating and editing a position — the fields, their validation and the
 * contact-override rule below are identical, and a second copy would drift.
 *
 * A position is creatable with no contact at all — "I need a camera operator, I do not know who
 * yet" is the headline capability here. `email`/`phone` are stored on the position itself (see
 * `app/entities/crew-member.ts`) and outrank the linked contact when the server resolves a row,
 * so they stay editable with or without one — what is typed is an override, not a dead letter.
 */
export const CrewMemberModal: React.FC<CrewMemberModalProps> = ({
  project,
  member,
  defaults,
  onCancel,
  onClose,
}) => {
  const isEdit = !!member;

  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Free-text-creatable, mirroring `RoleSelect` — seeded from the project's pick-lists, with an
  // ad hoc value appended locally when the user types one that is not there yet. An existing
  // member's own department/team (or a pre-filled `defaults` one) is seeded in too, so editing a
  // row whose value predates the pick-lists — or adding into a group whose value does — does not
  // silently blank it.
  const [departmentOptions, setDepartmentOptions] = useState(() => {
    const seed = member?.department ?? defaults?.department;
    return seed && !project.crewDepartments.includes(seed)
      ? [...project.crewDepartments, seed]
      : project.crewDepartments;
  });
  const [teamOptions, setTeamOptions] = useState(() => {
    const seed = member?.team ?? defaults?.team;
    return seed && !project.crewTeams.includes(seed)
      ? [...project.crewTeams, seed]
      : project.crewTeams;
  });

  const contacts = Space.getOne(project.spaceId)?.contacts || [];
  const contactOptions = getCrewContactOptions(project);

  const {
    handleSubmit,
    control,
    formState: { dirtyFields },
  } = useForm<FormValues>({
    mode: 'onSubmit',
    // `defaults` only ever seeds a *new* position — edit mode already has a real member and must
    // keep populating from it untouched.
    defaultValues: {
      name: member?.name ?? defaults?.name ?? '',
      role: member?.role ?? defaults?.role ?? '',
      department: member?.department ?? defaults?.department ?? '',
      team: member?.team ?? defaults?.team ?? '',
      email: member?.email ?? defaults?.email ?? '',
      phone: member?.phone ?? defaults?.phone ?? '',
      contactId: member?.contactId ?? defaults?.contactId ?? undefined,
    },
  });

  // Watched rather than read once: picking or clearing a contact has to swap the email/phone
  // inputs for the contact's own values live, not on the next open.
  const selectedContactId = useWatch({ control, name: 'contactId' });
  const selectedContact = selectedContactId
    ? contacts.find((contact) => contact._id === selectedContactId)
    : undefined;

  const onSubmit = async (values: FormValues) => {
    if (isLoading) return;

    if (!values.contactId && !values.name?.trim() && !values.role?.trim()) {
      setFormError('Add a contact, a name or a role.');
      return;
    }

    setFormError(null);
    setIsLoading(true);

    const newDepartment = values.department?.trim();
    const newTeam = values.team?.trim();

    try {
      if (member) {
        // Only the fields the user actually touched, via `dirtyFields` — never the whole form.
        // `member.name` (and the rest) arrive from the client already RESOLVED (see
        // `fetchCrew`/`resolve-crew-member.ts`): a blank stored name shows here as the contact's
        // name, or the role, as a fallback. Seeding the form from that and then PATCHing every
        // field back would write the fallback in as a real stored override on the very first
        // edit — undoing the fallback the moment anyone touches the row. Sending only dirty
        // fields is what keeps an untouched save a no-op. A field that IS touched still sends
        // `''` rather than being omitted when cleared — on a PATCH an absent field means "leave
        // it alone", so omitting a cleared field would silently restore the old value.
        const update: CrewMemberInput = {};

        if (dirtyFields.name) update.name = values.name?.trim() ?? '';
        if (dirtyFields.role) update.role = values.role?.trim() ?? '';
        if (dirtyFields.department) update.department = newDepartment ?? '';
        if (dirtyFields.team) update.team = newTeam ?? '';
        if (dirtyFields.email) update.email = values.email?.trim() ?? '';
        if (dirtyFields.phone) update.phone = values.phone?.trim() ?? '';
        if (dirtyFields.contactId) update.contactId = values.contactId || null;

        await updateCrewMember(member, update);
      } else {
        await createCrewMember(project._id, {
          name: values.name?.trim() || undefined,
          role: values.role?.trim() || undefined,
          department: newDepartment || undefined,
          team: newTeam || undefined,
          email: values.email?.trim() || undefined,
          phone: values.phone?.trim() || undefined,
          contactId: values.contactId || undefined,
        });
      }

      // Persist a newly typed department/team onto the project's pick-lists, so it survives a
      // reload and is choosable again next time. The picker itself already shows the new value
      // optimistically (`departmentOptions`/`teamOptions` above); this only settles the stored
      // list. Shared with the crew list's inline pickers, which do exactly the same thing.
      persistNewCrewPickListValues(project, {
        department: newDepartment,
        team: newTeam,
      }).catch(() => {
        // Non-fatal: the crew member was saved; only the pick-list failed to persist.
      });

      setIsLoading(false);
      onClose();
    } catch (e) {
      setIsLoading(false);
      catchError(
        e,
        isEdit
          ? 'Could not save this crew member. Try again.'
          : 'Could not add this crew member. Try again.',
      );
    }
  };

  return (
    <Dialog
      title={isEdit ? 'Edit crew member' : 'Add crew member'}
      onClose={onCancel}
      actions={[
        <TextButton key="cancel" text="Cancel" onClick={onCancel} />,
        <ElevatedButton
          key="submit"
          form="crew-member-form"
          type="submit"
          isLoading={isLoading}
          text={isEdit ? 'Save' : 'Add'}
        />,
      ]}
    >
      <form id="crew-member-form" className={styles['form']} onSubmit={handleSubmit(onSubmit)}>
        <Controller
          control={control}
          name="contactId"
          render={({ field: { value, onChange } }) => (
            <SingleSelectDropdown
              label="Contact"
              title="Contact"
              value={value}
              options={contactOptions}
              onValueChange={onChange}
              placeholder="No contact yet — leave it blank if you don't know who"
            />
          )}
        />

        <Controller
          control={control}
          name="name"
          render={({ field: { value, onChange } }) => (
            <TextInput
              label="Name"
              value={value ?? ''}
              onChange={onChange}
              placeholder="Overrides the contact's name, if any"
            />
          )}
        />

        <Controller
          control={control}
          name="role"
          render={({ field: { value, onChange } }) => (
            <RoleSelect value={value} onChange={onChange} />
          )}
        />

        <div className={styles['row']}>
          <Controller
            control={control}
            name="department"
            render={({ field: { value, onChange } }) => (
              <SingleSelectDropdown
                label="Department"
                title="Department"
                value={value}
                options={departmentOptions.map((department) => ({
                  label: department,
                  value: department,
                }))}
                onValueChange={onChange}
                onCreate={(inputValue) => {
                  setDepartmentOptions((options) =>
                    options.includes(inputValue) ? options : [...options, inputValue],
                  );
                  onChange(inputValue);
                }}
                placeholder="Choose from the list or use your own"
              />
            )}
          />

          <Controller
            control={control}
            name="team"
            render={({ field: { value, onChange } }) => (
              <SingleSelectDropdown
                label="Team"
                title="Team"
                value={value}
                options={teamOptions.map((team) => ({ label: team, value: team }))}
                onValueChange={onChange}
                onCreate={(inputValue) => {
                  setTeamOptions((options) =>
                    options.includes(inputValue) ? options : [...options, inputValue],
                  );
                  onChange(inputValue);
                }}
                placeholder="Choose from the list or use your own"
              />
            )}
          />
        </div>

        {/* Editable whether or not a contact is linked. `resolve-crew-member.ts` resolves a
            position's email and phone from its OWN stored value first and only falls back to the
            live contact, so what is typed here is a real override that survives the next read.
            When the field is blank, the contact's value shows as a placeholder rather than as a
            value: pre-filling it would turn the very first save into a frozen override and quietly
            stop the contact from ever updating this position again. */}
        <div className={styles['row']}>
          <Controller
            control={control}
            name="email"
            render={({ field: { value, onChange } }) => (
              <TextInput
                label="Email"
                type="email"
                value={value ?? ''}
                onChange={onChange}
                placeholder={selectedContact?.email || undefined}
              />
            )}
          />

          <Controller
            control={control}
            name="phone"
            render={({ field: { value, onChange } }) => (
              <TextInput
                label="Phone"
                type="tel"
                value={value ?? ''}
                onChange={onChange}
                placeholder={selectedContact?.phone || undefined}
              />
            )}
          />
        </div>

        {selectedContact && (
          <p className={styles['hint']}>Leave blank to use the contact&rsquo;s email and phone.</p>
        )}

        {formError && <p className={styles['error']}>{formError}</p>}
      </form>
    </Dialog>
  );
};
